import type { CalculateRegistryPurchaseInfoFragmentFragment } from '@graphql/fragments/registry.graphql.generated';
import type { MonetaryValueFragment } from '@graphql/fragments/monetary.graphql.generated';

const SHIPPING_AND_HANDLING_TITLES = {
  Delivery: 'Delivery',
  BulkItemSurcharge: 'Bulk Item Surcharge',
} as const;

type ShippingAndHandlingLine = CalculateRegistryPurchaseInfoFragmentFragment['shippingAndHandling'][0];

export type AdditionalCostLine = Pick<ShippingAndHandlingLine, 'lineValue' | 'title'> & { __typename: string };
type AdditionalCostLineBuckets = {
  shippingAndHandling: ShippingAndHandlingLine[];
  orderCharges: AdditionalCostLine[];
  postPurchaseFees: AdditionalCostLine[];
  compensation: AdditionalCostLine[];
};

const normalizeShippingAndHandlingTitle = (title: Maybe<string>) => title?.trim().toLowerCase() ?? '';
const deliveryTitleKey = SHIPPING_AND_HANDLING_TITLES.Delivery.toLowerCase();
const bulkItemSurchargeTitleKey = SHIPPING_AND_HANDLING_TITLES.BulkItemSurcharge.toLowerCase();

export const isDeliveryShippingAndHandlingTitle = (title: Maybe<string>) => normalizeShippingAndHandlingTitle(title) === deliveryTitleKey;

export const isBulkItemSurchargeShippingAndHandlingTitle = (title: Maybe<string>) => normalizeShippingAndHandlingTitle(title) === bulkItemSurchargeTitleKey;

/**
 * Sums the lineValue of the given lines into a single MonetaryValue.
 * Returns null when lines is empty so callers can distinguish "no lines" from "zero total".
 * Assumes all lines share the same currency.
 */
export const sumLineValues = (lines: AdditionalCostLine[]): MonetaryValueFragment => {
  if (lines.length === 0)
    return {
      valueInMinorUnits: 0,
      floatingPointDecimalString: '0.00',
      currency: null,
    };
  const currency = lines[0].lineValue.currency;
  const currencyPlaces = currency?.places ?? 2;
  const valueInMinorUnitsSum = lines.reduce((total, line) => total + line.lineValue.valueInMinorUnits, 0);
  const floatingPointDecimalString = (valueInMinorUnitsSum / Math.pow(10, currencyPlaces)).toFixed(currencyPlaces);
  return { valueInMinorUnits: valueInMinorUnitsSum, floatingPointDecimalString, currency };
};

type SplitShippingAndHandlingLinesResult = Readonly<{
  delivery: Readonly<{
    shippingLines: ShippingAndHandlingLine[];
    totalValue: MonetaryValueFragment;
  }>;
  bulkSurcharge: Readonly<{
    shippingLines: ShippingAndHandlingLine[];
    totalValue: MonetaryValueFragment;
  }>;
}>;

export const splitShippingAndHandlingLines = (shippingAndHandling: ShippingAndHandlingLine[] = []): SplitShippingAndHandlingLinesResult => {
  const deliveryLines: ShippingAndHandlingLine[] = [];
  const bulkSurchargeLines: ShippingAndHandlingLine[] = [];
  for (const line of shippingAndHandling) {
    if (isDeliveryShippingAndHandlingTitle(line.title)) {
      deliveryLines.push(line);
    } else if (isBulkItemSurchargeShippingAndHandlingTitle(line.title)) {
      bulkSurchargeLines.push(line);
    } else {
      console.warn('Unknown shipping and handling title:', line.title);
    }
  }
  return {
    delivery: { shippingLines: deliveryLines, totalValue: sumLineValues(deliveryLines) },
    bulkSurcharge: { shippingLines: bulkSurchargeLines, totalValue: sumLineValues(bulkSurchargeLines) },
  };
};

export type PurchaseInfoCostLines = Readonly<{
  deliveryShippingLines: ShippingAndHandlingLine[];
  deliveryTotalValue: MonetaryValueFragment;
  bulkSurchargeShippingLines: ShippingAndHandlingLine[];
  bulkSurchargeTotalValue: MonetaryValueFragment;
  additionalCostLines: AdditionalCostLine[];
  totalAdditionalCost: MonetaryValueFragment;
  orderedAllCostLines: AdditionalCostLine[];
  totalAllCost: MonetaryValueFragment;
  isFreeShipping: boolean;
}>;

export const computeRegistryPurchaseInfoCostLines = (calculateRegistryPurchaseInfo: Maybe<AdditionalCostLineBuckets>): PurchaseInfoCostLines | null => {
  if (!calculateRegistryPurchaseInfo) {
    return null;
  }
  const { orderCharges, postPurchaseFees, compensation, shippingAndHandling } = calculateRegistryPurchaseInfo;
  const { delivery, bulkSurcharge } = splitShippingAndHandlingLines(shippingAndHandling);
  const additionalCostLines = [...orderCharges, ...postPurchaseFees, ...compensation];
  const orderedAllCostLines = [...delivery.shippingLines, ...bulkSurcharge.shippingLines, ...additionalCostLines];
  return {
    deliveryShippingLines: delivery.shippingLines,
    deliveryTotalValue: delivery.totalValue,
    bulkSurchargeShippingLines: bulkSurcharge.shippingLines,
    bulkSurchargeTotalValue: bulkSurcharge.totalValue,
    additionalCostLines,
    totalAdditionalCost: sumLineValues(additionalCostLines),
    orderedAllCostLines,
    totalAllCost: sumLineValues(orderedAllCostLines),
    isFreeShipping: delivery.totalValue == null || delivery.totalValue.valueInMinorUnits === 0,
  };
};
